"use client";

import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ContentCard } from "@/features/marketing/components/content/MarketingContentCollection";
import type { MarketingContentItem } from "@/features/marketing/content/marketing-content";
import { cn } from "@/lib/utils";

type MarketingContentTab = {
	value: string;
	label: string;
	items: MarketingContentItem[];
};

interface MarketingContentTabsProps {
	tabs: MarketingContentTab[];
	defaultValue?: string;
	className?: string;
}

export function MarketingContentTabs({
	tabs,
	defaultValue = tabs[0]?.value,
	className
}: MarketingContentTabsProps) {
	const router = useRouter();
	const pathname = usePathname();
	const search_params = useSearchParams();

	const update_tab_param = (value: string) => {
		const next_params = new URLSearchParams(search_params?.toString());

		if (value === tabs[0]?.value) {
			next_params.delete("tab");
		} else {
			next_params.set("tab", value);
		}

		const query = next_params.toString();
		router.replace(query ? `${pathname}?${query}` : pathname, { scroll: false });
	};

	return (
		<section className={cn("w-full bg-slate-50/60 py-10 sm:py-14", className)}>
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<Tabs defaultValue={defaultValue} onValueChange={update_tab_param} className="w-full">
					<div className="mb-8 border-y border-slate-200 bg-white/70">
						<TabsList className="h-auto w-full justify-start gap-1 overflow-x-auto rounded-none bg-transparent p-0 text-slate-600">
							{tabs.map((tab) => (
								<TabsTrigger
									key={tab.value}
									value={tab.value}
									className="relative gap-2 rounded-none border-b-2 border-transparent px-4 py-4 text-sm font-semibold text-slate-500 shadow-none data-[state=active]:border-ss-blue data-[state=active]:bg-transparent data-[state=active]:text-slate-950 data-[state=active]:shadow-none"
								>
									<span>{tab.label}</span>
									<span className="rounded-full bg-slate-100 px-2 py-0.5 text-[0.68rem] font-semibold text-slate-500">
										{tab.items.length}
									</span>
								</TabsTrigger>
							))}
						</TabsList>
					</div>

					{tabs.map((tab) => (
						<TabsContent key={tab.value} value={tab.value} className="mt-0">
							<div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
								{tab.items.map((item) => (
									<ContentCard key={item.id} item={item} cardSize="large" />
								))}
							</div>
						</TabsContent>
					))}
				</Tabs>
			</div>
		</section>
	);
}
